<template>
  <div class="image-loader">
    <!-- Add photo buttons -->
    <div class="add-photo-row">
      <!-- Bottone fotocamera: solo su nativo -->
      <AeButton v-if="isMobile" variant="primary" @click="handleNativeCamera('camera')" :disabled="uploading">
        <AeIcon name="Camera" :size="16" />
        {{ uploading ? t('imageLoader.uploading', { current: uploadCurrent, total: uploadTotal }) : t('imageLoader.takePhoto') }}
      </AeButton>
      <!-- Bottone galleria -->
      <AeButton variant="primary" @click="isMobile ? handleNativeCamera('gallery') : triggerFileInput()" :disabled="uploading && !isMobile">
        <AeIcon name="Image" :size="16" />
        {{ !isMobile && uploading ? t('imageLoader.uploading', { current: uploadCurrent, total: uploadTotal }) : t('imageLoader.addPhoto') }}
      </AeButton>

      <!-- Input per galleria (web) -->
      <input
        v-if="!isMobile"
        ref="fileInput"
        type="file"
        accept="image/*"
        multiple
        class="hidden-input"
        @change="onFilesSelected"
      />

      <span v-if="queueCount > 0" class="queue-badge" :title="t('imageLoader.queuedTitle')">
        <AeIcon name="CloudOff" :size="12" />
        {{ t('imageLoader.queuedCount', { count: queueCount }) }}
      </span>
    </div>

    <!-- Upload progress -->
    <div v-if="uploading" class="upload-progress">
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: uploadPercent + '%' }"></div>
      </div>
      <span class="progress-text">{{ t('imageLoader.progressText', { current: uploadCurrent, total: uploadTotal }) }}</span>
    </div>

    <!-- Upload errors -->
    <div v-if="uploadErrors.length > 0" class="upload-errors">
      <div v-for="(err, i) in uploadErrors" :key="i" class="error-item">
        <AeIcon name="AlertCircle" :size="14" />
        <span>{{ err }}</span>
        <button class="dismiss-btn" @click="uploadErrors.splice(i, 1)">&times;</button>
      </div>
    </div>

    <!-- Image grid -->
    <div v-if="images.length > 0" class="image-grid">
      <div
        v-for="(img, index) in images"
        :key="img.guid"
        class="image-thumb"
        @click="openPreview(img, index)"
      >
        <img :src="img.src" alt="" />
        <div v-if="!img.synced" class="sync-badge" :title="t('imageLoader.notSynced')">
          <AeIcon name="CloudOff" :size="12" />
        </div>
        <div v-if="img.description" class="thumb-caption">{{ img.description }}</div>
      </div>
    </div>
    <div v-else-if="!uploading" class="no-images">
      <p>{{ t('imageLoader.noImages') }}</p>
    </div>

    <!-- Preview modal -->
    <AeModal v-model="showModal" :title="t('imageLoader.modalTitle')" size="large" flex-body>
      <div v-if="selectedImage" class="preview-content">
        <img :src="selectedImage.src" alt="" class="preview-image" />
        <div class="caption-group">
          <label>{{ t('imageLoader.captionLabel') }}</label>
          <input
            type="text"
            v-model="captionDraft"
            :placeholder="t('imageLoader.captionPlaceholder')"
            @blur="saveCaption"
          />
        </div>
        <div class="sync-status" :class="selectedImage.synced ? 'synced' : 'not-synced'">
          <AeIcon :name="selectedImage.synced ? 'Cloud' : 'CloudOff'" :size="14" />
          {{ selectedImage.synced ? t('imageLoader.syncedWithServer') : t('imageLoader.notSynced') }}
        </div>
      </div>
      <template #footer>
        <div class="modal-actions">
          <AeButton variant="success" @click="closeDialog">
            <AeIcon name="Check" :size="14" />
            {{ t('imageLoader.save') }}
          </AeButton>
          <AeButton variant="danger" size="small" @click="deleteImage">
            <AeIcon name="Trash2" :size="14" />
            {{ t('imageLoader.delete') }}
          </AeButton>
        </div>
      </template>
    </AeModal>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { AeButton, AeModal, AeIcon } from '@aevue/components'
import { api, BASE_URL } from '../services/api.js'
import { useAppStore } from '../stores/app.js'
import { useDbStore } from '../stores/dbStore.js'
import { isNative } from '../services/platform.js'
import { useImages } from '../composables/useImages.js'

const { t } = useI18n()

const props = defineProps({
  type: { type: String, required: true },
  record: { type: String, required: true },
  modelValue: { type: Array, default: () => [] },
  // Se true: quando l'upload termina dopo che `record` è già cambiato (es. l'utente
  // è passato alla scheda successiva), prova ad aggiornare direttamente il record
  // originale su IndexedDB invece di ignorare il risultato. Se il record non viene
  // trovato (mai salvato) non fa nulla. Disattivato di default per non introdurre
  // effetti collaterali negli altri usi di questo componente.
  reconcileAfterNavigate: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue'])

const appStore = useAppStore()
const dbStore = useDbStore()
const { pickImage, uploadImage, flushQueue, queueCount } = useImages()

const isMobile = isNative()
const fileInput = ref(null)
const images = ref([])
// Conteggio upload in corso per record (guid -> numero upload attivi). Permette a `uploading`
// di riflettere solo gli upload relativi al record attualmente visualizzato: se l'utente
// naviga a un record diverso mentre un upload precedente è ancora in volo, quel record smette
// subito di risultare "in caricamento" (l'upload prosegue comunque in background).
const uploadingRecords = reactive(new Map())
function markUploadStart(record) {
  uploadingRecords.set(record, (uploadingRecords.get(record) || 0) + 1)
}
function markUploadEnd(record) {
  const n = (uploadingRecords.get(record) || 0) - 1
  if (n <= 0) uploadingRecords.delete(record)
  else uploadingRecords.set(record, n)
}
const uploading = computed(() => uploadingRecords.has(props.record))
const uploadCurrent = ref(0)
const uploadTotal = ref(0)
const uploadPercent = ref(0)
const uploadErrors = ref([])
const showModal = ref(false)
const selectedImage = ref(null)
const selectedIndex = ref(null)
const captionDraft = ref('')

function getImageUrl(guid) {
  return `${BASE_URL}/static/uploads/images/${guid}.png`
}

function parseItem(item) {
  if (typeof item === 'string') {
    return { guid: item, description: '', synced: true }
  }
  return {
    guid: item.guid,
    description: item.description || '',
    synced: item.synced !== false
  }
}

function toModelItem(img) {
  if (!img.description && img.synced) {
    return img.guid
  }
  const item = { guid: img.guid }
  if (img.description) item.description = img.description
  if (!img.synced) item.synced = false
  return item
}

function triggerFileInput() {
  fileInput.value?.click()
}

function loadImages() {
  const currentMap = new Map(images.value.map(i => [i.guid, i]))
  images.value = (props.modelValue || []).map(item => {
    const parsed = parseItem(item)
    const existing = currentMap.get(parsed.guid)
    // Preserve base64 src for images not yet on the server (added offline)
    const src = (existing?.src?.startsWith('data:') && !parsed.synced)
      ? existing.src
      : getImageUrl(parsed.guid)
    return {
      guid: parsed.guid,
      description: parsed.description,
      synced: parsed.synced,
      src
    }
  })
}

watch(() => props.modelValue, () => {
  loadImages()
}, { deep: true })

function emitCurrentModel() {
  emit('update:modelValue', images.value.map(toModelItem))
}

// ── Handler camera nativa ────────────────────────────────────────────────────

async function handleNativeCamera(source = 'camera') {
  if (uploading.value) return
  try {
    const dataUrl = await pickImage(source)
    if (!dataUrl) return
    await _processImage(dataUrl, source === 'gallery' ? 'galleria' : 'foto')
  } catch (err) {
    if (err.message && err.message.includes('cancelled')) return
    const sourceLabel = source === 'gallery' ? t('imageLoader.sourceGallery') : t('imageLoader.sourceCamera')
    uploadErrors.value.push(t('imageLoader.errorSource', { source: sourceLabel, message: err.message }))
  }
}

// ── Handler file input (web) ─────────────────────────────────────────────────

async function onFilesSelected(event) {
  const files = event.target.files
  if (!files || files.length === 0) return

  uploadTotal.value = files.length
  uploadCurrent.value = 0
  uploadPercent.value = 0
  uploadErrors.value = []

  for (const file of files) {
    uploadCurrent.value++
    uploadPercent.value = Math.round((uploadCurrent.value / uploadTotal.value) * 100)

    let base64Data
    try {
      base64Data = await fileToBase64(file)
    } catch (err) {
      uploadErrors.value.push(t('imageLoader.errorReadingFile', { name: file.name }))
      continue
    }

    await _processImage(base64Data, file.name)
  }

  if (fileInput.value) fileInput.value.value = ''
}

// ── Logica comune upload / accodamento ───────────────────────────────────────

async function _processImage(base64Data, label) {
  if (images.value.length >= 20) {
    uploadErrors.value.push(t('imageLoader.errorQueueLimit'))
    return
  }

  uploadErrors.value = []

  const guid = crypto.randomUUID()
  const targetRecord = props.record
  markUploadStart(targetRecord)

  console.log('[IMG_REC] avvio upload', { guid, targetRecord, type: props.type })

  try {
    const result = await uploadImage(guid, base64Data, {
      record: targetRecord,
      type: props.type,
      note: ''
    })

    console.log('[IMG_REC] upload risolto', { guid, targetRecord, currentRecord: props.record, result })

    // Caso comune: siamo ancora sullo stesso record, si aggiorna la UI corrente
    if (props.record === targetRecord) {
      console.log('[IMG_REC] record invariato, aggiorno la UI corrente', { guid })
      if (result.queued) {
        images.value.push({ guid, description: '', synced: false, src: base64Data })
        emitCurrentModel()
      } else if (result.ok) {
        images.value.push({ guid, description: '', synced: true, src: base64Data })
        emitCurrentModel()
      } else {
        uploadErrors.value.push(t('imageLoader.errorServer', { label }))
      }
      return
    }

    // Il record è cambiato nel frattempo (es. navigazione alla pianta successiva
    // prima del completamento dell'upload). Non tocchiamo `images.value` (rappresenta
    // ormai un altro record) ma, se abilitato, proviamo ad aggiornare direttamente
    // il record originale su IndexedDB — se non c'è (mai salvato) non facciamo nulla.
    console.log('[IMG_REC] record cambiato durante upload', { guid, targetRecord, currentRecord: props.record, reconcileEnabled: props.reconcileAfterNavigate })
    if (props.reconcileAfterNavigate && (result.ok || result.queued)) {
      await _reconcileRecordImages(targetRecord, guid, /* synced */ result.ok && !result.queued)
    } else {
      console.log('[IMG_REC] riconciliazione saltata (disabilitata o upload non riuscito)', { guid, targetRecord })
    }
  } catch (err) {
    if (props.record === targetRecord) {
      uploadErrors.value.push(t('imageLoader.errorUpload', { label, message: err.message || t('imageLoader.errorNetwork') }))
    } else {
      console.warn('[IMG_REC] upload fallito dopo cambio record, immagine scartata', { guid, targetRecord, err })
    }
  } finally {
    markUploadEnd(targetRecord)
  }
}

// Aggiorna il campo images di un record diverso da quello attualmente mostrato,
// dopo che l'upload è terminato con l'utente già passato al record successivo.
async function _reconcileRecordImages(recordId, guid, synced) {
  console.log('[IMG_REC] riconciliazione: cerco il record', { recordId, guid })
  const rec = await dbStore.selectOne(props.type, recordId)
  if (!rec.ok || !rec.data) {
    console.log('[IMG_REC] riconciliazione: record non trovato, nessuna modifica', { recordId, guid })
    return
  }

  const existingImages = Array.isArray(rec.data.images) ? rec.data.images : []
  if (existingImages.some(i => (typeof i === 'string' ? i : i.guid) === guid)) {
    console.log('[IMG_REC] riconciliazione: guid già presente, nessuna modifica', { recordId, guid })
    return
  }

  const newItem = synced ? guid : { guid, synced: false }
  const r = await dbStore.update(props.type, recordId, { images: [...existingImages, newItem] })
  console.log('[IMG_REC] riconciliazione: record aggiornato', { recordId, guid, synced, ok: r.ok })
}

// ── Online listener ──────────────────────────────────────────────────────────

async function handleOnline() {
  await flushQueue(images)
  // Mark as synced any offline images no longer in queue (might have been flushed globally)
  const queueResult = await dbStore.select('image_upload_queue')
  if (queueResult.ok) {
    const queuedGuids = new Set(queueResult.data.map(i => i.guid_image))
    let changed = false
    for (const img of images.value) {
      if (!img.synced && !queuedGuids.has(img.guid)) {
        img.synced = true
        changed = true
      }
    }
    // Emette solo se qualcosa è effettivamente cambiato (evita loop con poderi:sync-complete)
    if (changed) emitCurrentModel()
  }
}

onMounted(async () => {
  console.log('[ImageLoader] onMounted start')
  loadImages()
  window.addEventListener('poderi:sync-complete', handleOnline)
  await nextTick()
  console.log('[ImageLoader] after nextTick, before select image_upload_queue')
  // Al mount: aggiorna synced per immagini che nel frattempo sono già state caricate (fuori componente)
  const queueResult = await dbStore.select('image_upload_queue')
  console.log('[ImageLoader] after select image_upload_queue', queueResult?.ok)
  if (queueResult.ok) {
    const queuedGuids = new Set(queueResult.data.map(i => i.guid_image))
    let changed = false
    for (const img of images.value) {
      if (!img.synced && !queuedGuids.has(img.guid)) { img.synced = true; changed = true }
    }
    if (changed) emitCurrentModel()
  }
})

onUnmounted(() => {
  window.removeEventListener('poderi:sync-complete', handleOnline)
})

// ── Utilities ────────────────────────────────────────────────────────────────

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

function openPreview(img, index) {
  selectedImage.value = img
  selectedIndex.value = index
  captionDraft.value = img.description || ''
  showModal.value = true
}

async function saveCaption() {
  if (!selectedImage.value) return
  if (captionDraft.value === selectedImage.value.description) return

  selectedImage.value.description = captionDraft.value
  if (selectedIndex.value !== null) {
    images.value[selectedIndex.value].description = captionDraft.value
  }

  await api.post(`/api/v2/poderi2/store_images/${selectedImage.value.guid}`, {
    access_token: appStore.accessToken,
    base64: 'data:image/png;base64,',
    guid_survey: props.record,
    table_ref: props.type,
    note: captionDraft.value
  })

  emitCurrentModel()
}

function deleteImage() {
  if (!selectedImage.value || selectedIndex.value === null) return
  images.value.splice(selectedIndex.value, 1)
  emitCurrentModel()
  showModal.value = false
  selectedImage.value = null
  selectedIndex.value = null
}

function closeDialog() {
  showModal.value = false
  selectedImage.value = null
  selectedIndex.value = null
}

defineExpose({ uploading })
</script>

<style scoped>
.image-loader {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.add-photo-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.hidden-input {
  display: none;
}

.queue-badge {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.78rem;
  color: #c05621;
  background: rgba(221, 107, 32, 0.1);
  border: 1px solid rgba(221, 107, 32, 0.25);
  border-radius: 12px;
  padding: 0.2rem 0.6rem;
}

/* Progress */
.upload-progress {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: var(--border-color, #e2e8f0);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--primary-color, #4299e1);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.8rem;
  color: var(--text-muted, #999);
  white-space: nowrap;
}

/* Errors */
.upload-errors {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.error-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: rgba(229, 62, 62, 0.08);
  border: 1px solid rgba(229, 62, 62, 0.25);
  border-radius: var(--border-radius, 8px);
  color: #c53030;
  font-size: 0.85rem;
}

.error-item span {
  flex: 1;
}

.dismiss-btn {
  background: none;
  border: none;
  color: #c53030;
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0 0.25rem;
  line-height: 1;
  opacity: 0.6;
}

.dismiss-btn:hover {
  opacity: 1;
}

/* Grid */
.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.75rem;
}

.image-thumb {
  position: relative;
  aspect-ratio: 1;
  border-radius: var(--border-radius, 8px);
  overflow: hidden;
  cursor: pointer;
  border: 1px solid var(--border-color, #e2e8f0);
  transition: transform 0.15s, box-shadow 0.15s;
}

.image-thumb:hover {
  transform: scale(1.03);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.image-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sync-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(221, 107, 32, 0.85);
  color: white;
  border-radius: 50%;
}

.thumb-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0.25rem 0.4rem;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  font-size: 0.7rem;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.no-images {
  text-align: center;
  padding: 1.5rem;
  color: var(--text-muted, #999);
}

/* Modal */
.preview-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.preview-image {
  width: 100%;
  max-height: 60vh;
  object-fit: contain;
  border-radius: var(--border-radius, 8px);
}

.caption-group {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.caption-group label {
  font-weight: 500;
  font-size: 0.85rem;
  color: var(--text-color);
}

.caption-group input {
  padding: 0.6rem 0.8rem;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: var(--border-radius, 8px);
  font-family: var(--font-family);
  background: rgba(255, 255, 255, 0.5);
  color: var(--text-color);
}

.caption-group input:focus {
  outline: none;
  border-color: var(--primary-color, #4299e1);
}

.sync-status {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  padding: 0.4rem 0.6rem;
  border-radius: var(--border-radius, 8px);
}

.sync-status.synced {
  color: #2f855a;
  background: rgba(47, 133, 90, 0.08);
}

.sync-status.not-synced {
  color: #c05621;
  background: rgba(221, 107, 32, 0.08);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
}
</style>
